/**
 * Review Prioritization & Scoring Algorithm
 * Helps users focus on high-impact reviews first
 */

/**
 * Calculate priority score for a review (0-100, higher = more urgent)
 * Factors:
 * - Low ratings (1-2 stars) = high priority
 * - Unanswered reviews = high priority (older = more urgent)
 * - Mixed sentiment (negative with detail) = high priority
 */
export function calculateReviewScore(review, baseDate = new Date()) {
  let score = 50; // Base score
  
  // Factor 1: Rating (0-30 points)
  if (review.rating <= 2) {
    score += 30; // Low ratings need immediate attention
  } else if (review.rating === 3) {
    score += 15; // Medium ratings
  } else if (review.rating >= 4) {
    score += 5; // High ratings (smaller boost for tone)
  }
  
  // Factor 2: Response status (0-25 points)
  if (!review.has_reply) {
    // Calculate age of unanswered review
    const reviewDate = new Date(review.review_posted_at);
    const hoursOld = (baseDate - reviewDate) / (1000 * 60 * 60);
    
    if (hoursOld > 168) { // More than 1 week
      score += 25;
    } else if (hoursOld > 72) { // More than 3 days
      score += 20;
    } else if (hoursOld > 24) { // More than 1 day
      score += 15;
    } else {
      score += 10;
    }
  }
  
  // Factor 3: Text sentiment (0-20 points)
  const negativeKeywords = ['bad', 'terrible', 'awful', 'worse', 'disappointing', 'rude', 'never', 'worst'];
  const reviewText = (review.review_text || '').toLowerCase();
  const negativeCount = negativeKeywords.filter(keyword => reviewText.includes(keyword)).length;
  
  if (negativeCount >= 3 && review.rating <= 2) {
    score += 20; // High priority negative review
  } else if (negativeCount >= 1 && review.rating <= 3) {
    score += 10;
  }
  
  // Factor 4: Review length (0-15 points) - longer = more detailed complaint/praise worth addressing
  const textLength = (review.review_text || '').length;
  if (textLength > 200) {
    score += 15;
  } else if (textLength > 100) {
    score += 10;
  }
  
  return Math.min(score, 100); // Cap at 100
}

/**
 * Sort reviews by priority
 */
export function sortReviewsByPriority(reviews) {
  const baseDate = new Date();
  
  return [...reviews].sort((a, b) => {
    const scoreA = calculateReviewScore(a, baseDate);
    const scoreB = calculateReviewScore(b, baseDate);
    
    // Higher score first (more urgent)
    if (scoreA !== scoreB) {
      return scoreB - scoreA;
    }
    
    // If scores equal, sort by date (older first)
    return new Date(a.review_posted_at) - new Date(b.review_posted_at);
  });
}

/**
 * Get recommendation badge for a review
 */
export function getRecommendationBadge(review, baseDate = new Date()) {
  const score = calculateReviewScore(review, baseDate);
  
  if (score >= 80) {
    return { label: '🔴 Urgent', color: 'red', description: 'High priority - needs immediate response' };
  } else if (score >= 60) {
    return { label: '🟡 Important', color: 'yellow', description: 'Important to address soon' };
  } else if (score >= 40) {
    return { label: '🟢 Normal', color: 'green', description: 'Standard priority' };
  } else {
    return { label: '⚪ Low', color: 'gray', description: 'Can be addressed later' };
  }
}

/**
 * Filter reviews by priority level
 */
export function filterByPriority(reviews, minScore) {
  return reviews.filter(review => calculateReviewScore(review) >= minScore);
}
